import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/Addons.js'

import vertexShader from "@/shader/main.vert"
import fragmentShader from "@/shader/main.frag"
import commonShader from "@/shader/common.frag"
import { Fish, getEdgeness } from './Fish/Fish'
import { COLOR_BACKGROUND, POND_SIZE, NB_FISHES, POND_SIZE_USABLE, SIZES } from './const'
import { mousePosition } from './utils/mouse'

const intersectPosition: THREE.Vector3 = new THREE.Vector3(0, 0, 0)

let isFollowMode = true
const CAMERA_FACTOR = 2

export class SceneManager {
    canvas: HTMLCanvasElement
    scene: THREE.Scene
    camera: THREE.OrthographicCamera
    renderer: THREE.WebGLRenderer
    // controls: OrbitControls
    clock: THREE.Clock
    timer: THREE.Timer
    raycaster: THREE.Raycaster

    // References for animation
    shaderMaterial: THREE.ShaderMaterial | null = null;
    panelMesh: THREE.Mesh | null = null;
    requestID: number | null = null;

    fishes: Fish[] = []

    fishFollowedId: number | undefined = 0

    fishFollowed: Fish | null = null

    constructor(canvas: HTMLCanvasElement) {
        this.canvas = canvas

        // 1. Base Setup
        this.scene = new THREE.Scene()
        this.scene.background = new THREE.Color(COLOR_BACKGROUND)

        const factor = SIZES.height / SIZES.width
        this.camera = new THREE.OrthographicCamera(
            -1 * CAMERA_FACTOR,
            1 * CAMERA_FACTOR,
            factor * CAMERA_FACTOR,
            -factor * CAMERA_FACTOR
            , 0.1, 100)
        this.camera.position.set(0, 3, 0)
        this.camera.lookAt(0, 0, 0)

        this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true })
        this.renderer.setSize(window.innerWidth, window.innerHeight)
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)) // Limit pixel ratio for performance

        let gl = this.renderer.getContext()
        gl.enable(gl.BLEND)
        this.renderer.getContext().blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)

        // this.controls = new OrbitControls(this.camera, this.canvas)
        // this.controls.enableDamping = true

        this.clock = new THREE.Clock()
        this.clock.autoStart = true
        this.clock.start()

        this.timer = new THREE.Timer()
        this.timer.connect(document)

        this.raycaster = new THREE.Raycaster()

        // 2. Build the World
        this.addLights()
        this.addShaderPanel()
        this.addFishes()

        // 3. Event Listeners
        window.addEventListener('resize', this.onResize.bind(this))

        // 4. Start Loop
        this.tick()

        this.canvas.addEventListener('click', (event: MouseEvent) => {

            mousePosition.x = event.clientX / SIZES.width * 2 - 1
            mousePosition.y = - (event.clientY / SIZES.height) * 2 + 1
            this.raycaster.setFromCamera(mousePosition, this.camera)
            console.log(mousePosition)

            const intersectedObjects = this.raycaster.intersectObjects<Fish>(this.fishes)

            if (intersectedObjects.length === 0) {
                this.fishFollowed = null
            } else {
                const fish = intersectedObjects[0].object
                fish.onClick(fish)
            }
        })
    }

    private addLights() {
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.5)
        this.scene.add(ambientLight)
        const pointLight = new THREE.PointLight(0xffffff, 200, 100)
        pointLight.position.set(0, 0, 0)
        this.scene.add(pointLight)
    }

    private addShaderPanel() {
        this.shaderMaterial = new THREE.RawShaderMaterial({
            vertexShader,
            fragmentShader: commonShader + fragmentShader,
            uniforms: { uTime: { value: 0 }, uIntersectPosition: { value: intersectPosition } },
            side: THREE.DoubleSide,
            transparent: true,
            depthWrite: false
        })

        this.panelMesh = new THREE.Mesh(new THREE.PlaneGeometry(POND_SIZE, POND_SIZE), this.shaderMaterial)
        this.panelMesh.position.y = 0
        this.panelMesh.rotateX(Math.PI / 2)
        this.scene.add(this.panelMesh)
    }

    private addFishes() {
        for (let i = 0; i < NB_FISHES; i++) {
            this.fishes.push(new Fish(
                this.scene,
                (fish: Fish) => {
                    this.fishFollowed = fish
                },
                (fish: Fish) => {
                    fish.isHovered = true
                }, i))
        }
    }

    private onResize() {
        const factor = window.innerHeight / window.innerWidth
        this.camera.top = factor * CAMERA_FACTOR
        this.camera.bottom = -factor * CAMERA_FACTOR
        this.camera.updateProjectionMatrix()
        this.renderer.setSize(window.innerWidth, window.innerHeight)
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
        SIZES.width = window.innerWidth
        SIZES.height = window.innerHeight
    }

    private tick() {
        this.timer.update()
        const elapsedTime = this.timer.getElapsed()
        const delta = this.timer.getDelta()

        // Targeted fish
        this.raycaster.setFromCamera(mousePosition, this.camera)

        const intersectedObjects = this.raycaster.intersectObjects<Fish>(this.fishes)

        if (intersectedObjects.length > 0) {
            const fish = intersectedObjects[0].object
            fish.onHover(fish)
            document.body.style.cursor = 'pointer'
        } else {
            document.body.style.cursor = 'auto'
        }

        if (this.fishFollowed) {
            this.camera.position.x = this.fishFollowed.position.x
            this.camera.position.z = this.fishFollowed.position.z
        }

        // Light on panel
        if (this.panelMesh != null) {
            const objectsToTest = [this.panelMesh]
            const intersects = this.raycaster.intersectObjects(objectsToTest)

            const intersection = intersects[0]

            if (intersection != null) {
                intersectPosition.copy(intersection.point)
            }
        }

        const isFishFollowedValid = this.fishFollowed != null

        const lightCenter = isFollowMode && isFishFollowedValid ? this.fishFollowed!.position : intersectPosition

        if (this.shaderMaterial) {
            this.shaderMaterial.uniforms.uTime.value = elapsedTime
            this.shaderMaterial.uniforms.uIntersectPosition.value = lightCenter
        }

        // this.controls.update()
        this.renderer.render(this.scene, this.camera)

        this.fishes.forEach(fish => fish.update(delta, elapsedTime, lightCenter))

        this.requestID = requestAnimationFrame(this.tick.bind(this))
    }

    destroy() {
        window.removeEventListener('resize', this.onResize.bind(this))
        if (this.requestID) cancelAnimationFrame(this.requestID)
        this.renderer.dispose()
    }
}